import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import Logo from '../components/Logo';
import ChatbotWidget from '../components/ChatbotWidget';

const ContactUs: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create mailto link with form data
    const { name, email, subject, message } = formData;
    const mailtoLink = `mailto:info@socialinone.ai?subject=${encodeURIComponent(subject || 'Contact Form Submission')}&body=${encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`
    )}`;
    window.location.href = mailtoLink;
  };

  return (
    <>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(99, 102, 241, 0.08))' }}>
        {/* Header */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            py: 2,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <Logo height={48} />
              </Box>
              <Stack direction="row" spacing={3} alignItems="center">
                <Button
                  variant="text"
                  onClick={() => navigate('/')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': {
                      background: 'rgba(16, 185, 129, 0.05)',
                    },
                  }}
                >
                  Home
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                    color: '#10B981',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      borderColor: '#10B981',
                      background: 'rgba(16, 185, 129, 0.05)',
                    },
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Page Title Section */}
        <Box
          sx={{
            pt: 14,
            pb: 0,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
                }}
              >
                <EmailIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Contact Us
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Get in touch with our team
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 6 }}>
            {/* Contact Information */}
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1f2937' }}>
                  Get in Touch
                </Typography>
                
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #10B981, #059669)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        info@socialinone.ai
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocationIcon sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Remote Team
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Response Time
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Within 24 hours
                      </Typography>
                    </Box>
                  </Box>
                </Stack>

                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Follow us on social media
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      component="a"
                      href="https://www.facebook.com/socialinone"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#1877F2',
                        '&:hover': { background: 'rgba(24, 119, 242, 0.1)' },
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://www.instagram.com/socialinone/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#E1306C',
                        '&:hover': { background: 'rgba(225, 48, 108, 0.1)' },
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                    <IconButton
                      component="a"
                      href="https://www.youtube.com/@SocialinOne"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#FF0000',
                        '&:hover': { background: 'rgba(255, 0, 0, 0.1)' },
                      }}
                    >
                      <YouTubeIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            {/* Contact Form */}
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1f2937' }}>
                  Send us a Message
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                    <Box>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#10B981',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        label="Your Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#10B981',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#10B981',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#10B981',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{
                          background: 'linear-gradient(135deg, #10B981, #059669)',
                          color: 'white',
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 2,
                          boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            boxShadow: '0 12px 20px rgba(16, 185, 129, 0.3)',
                          },
                        }}
                      >
                        Send Message
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Container>

      {/* Footer */}
      <Box
        id="footer"
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
          color: 'white',
          mt: 12,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, 
            gap: 4,
            justifyContent: { xs: 'center', md: 'stretch' }
          }}>
            {/* Company Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Logo height={32} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 3,
                    maxWidth: { xs: '100%', md: '64%' },
                  }}
                >
                  Transform your social media presence with AI-powered automation. 
                  Engage with your audience 24/7 and grow your brand effortlessly.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                  <IconButton
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#1877F2' },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/socialinone/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#E1306C' },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.tiktok.com/@socialinone"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00f2ea' },
                    }}
                  >
                    <Box
                      component="svg"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      sx={{ width: 24, height: 24, fill: 'currentColor' }}
                    >
                      <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z"/>
                    </Box>
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.youtube.com/@SocialinOne"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#FF0000' },
                    }}
                  >
                    <YouTubeIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>


            {/* Support */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Support
              </Typography>
              <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  FAQ
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  component={Link}
                  to="/help-center"
                  variant="text"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Help Center
                </Button>
              </Stack>
            </Box>



            {/* Contact Info */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    info@socialinone.ai
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <LocationIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Almere, Netherlands
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'center' },
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              © 2025 NowUp. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 3 },
                ml: { xs: 0, md: 0 },
              }}
            >
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/terms-of-use')}
              >
                Terms of Service
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/cookie-policy')}
              >
                Cookie Policy
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      </Box>
      <ChatbotWidget />
    </>
  );
};

export default ContactUs;
